import React from 'react';
import { CheckCircle } from 'lucide-react';
import useOperatingMarkets from '../../../hooks/useOperatingMarkets';

/** Strips letters and symbols; keeps digits and common phone formatting. */
const sanitizePhoneInput = (value) =>
  String(value ?? '').replace(/[^\d+()\s.-]/g, '');

const CUSTOMER_TYPE_OPTIONS = [
  { value: 'individual', label: 'Individual' },
  { value: 'business', label: 'Business' },
];

const ContactInfoForm = ({
  bookingData,
  errors,
  onInputChange,
  user,
  requiredFields = [],
}) => {
  const { options: countryOptions, isLoading: marketsLoading, isEmpty } = useOperatingMarkets({
    bookableOnly: true,
  });
  const isBusiness = bookingData.customerType === 'business';
  const req = (field) => requiredFields.includes(field);
  const marketError =
    errors.billingCountry || (isEmpty ? 'No markets are open for bookings' : '');

  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <h2 className="marketing-display-md text-m-ink">
          Contact information
        </h2>
        {user && (
          <div className="inline-flex h-7 items-center rounded-full border border-m-border-light bg-m-canvas-light px-2.5 text-xs text-m-ink">
            <CheckCircle className="mr-1.5 h-3.5 w-3.5" />
            Auto-filled
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <div>
          <label className="auth-field-label" htmlFor="booking-first-name">
            First name <span className="text-red-500">*</span>
          </label>
          <input
            id="booking-first-name"
            type="text"
            value={bookingData.contactInfo.firstName || ''}
            onChange={(e) => onInputChange('contactInfo.firstName', e.target.value)}
            placeholder="Enter your first name"
            required
            className="auth-input"
          />
          {errors['contactInfo.firstName'] && (
            <p className="mt-1 text-xs text-red-600">{errors['contactInfo.firstName']}</p>
          )}
        </div>

        <div>
          <label className="auth-field-label" htmlFor="booking-last-name">
            Last name <span className="text-red-500">*</span>
          </label>
          <input
            id="booking-last-name"
            type="text"
            value={bookingData.contactInfo.lastName || ''}
            onChange={(e) => onInputChange('contactInfo.lastName', e.target.value)}
            placeholder="Enter your last name"
            required
            className="auth-input"
          />
          {errors['contactInfo.lastName'] && (
            <p className="mt-1 text-xs text-red-600">{errors['contactInfo.lastName']}</p>
          )}
        </div>

        <div>
          <label className="auth-field-label" htmlFor="booking-email">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            id="booking-email"
            type="email"
            value={bookingData.contactInfo.email || ''}
            onChange={(e) => onInputChange('contactInfo.email', e.target.value)}
            placeholder="Enter your email"
            required
            className="auth-input"
          />
          {errors['contactInfo.email'] && (
            <p className="mt-1 text-xs text-red-600">{errors['contactInfo.email']}</p>
          )}
        </div>

        <div>
          <label className="auth-field-label" htmlFor="booking-phone">
            Phone <span className="text-red-500">*</span>
          </label>
          <input
            id="booking-phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={bookingData.contactInfo.phone || ''}
            onChange={(e) =>
              onInputChange('contactInfo.phone', sanitizePhoneInput(e.target.value))
            }
            placeholder="Enter your phone number"
            required
            className="auth-input"
          />
          {errors['contactInfo.phone'] && (
            <p className="mt-1 text-xs text-red-600">{errors['contactInfo.phone']}</p>
          )}
        </div>

        <div>
          <label className="auth-field-label" htmlFor="booking-billing-market">
            Billing market {req('billing_country') && <span className="text-red-500">*</span>}
          </label>
          <select
            id="booking-billing-market"
            value={bookingData.billingCountry || ''}
            onChange={(e) => onInputChange('billingCountry', e.target.value)}
            disabled={marketsLoading || isEmpty}
            required={req('billing_country')}
            className="auth-input"
          >
            <option value="">
              {marketsLoading ? 'Loading markets…' : 'Select market'}
            </option>
            {countryOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {marketError && (
            <p className="mt-1 text-xs text-red-600">{marketError}</p>
          )}
        </div>

        <div>
          <label className="auth-field-label" htmlFor="booking-customer-type">
            Customer type {req('customer_type') && <span className="text-red-500">*</span>}
          </label>
          <select
            id="booking-customer-type"
            value={bookingData.customerType || 'individual'}
            onChange={(e) => onInputChange('customerType', e.target.value)}
            required={req('customer_type')}
            className="auth-input"
          >
            {CUSTOMER_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {errors.customerType && (
            <p className="mt-1 text-xs text-red-600">{errors.customerType}</p>
          )}
        </div>

        {(isBusiness || req('customer_tax_id')) && (
          <div className="md:col-span-2">
            <label className="auth-field-label" htmlFor="booking-tax-id">
              Business tax ID {req('customer_tax_id') && <span className="text-red-500">*</span>}
            </label>
            <input
              id="booking-tax-id"
              type="text"
              value={bookingData.customerTaxId || ''}
              onChange={(e) => onInputChange('customerTaxId', e.target.value)}
              placeholder="VAT / GST / EIN"
              required={req('customer_tax_id')}
              className="auth-input"
            />
            {errors.customerTaxId && (
              <p className="mt-1 text-xs text-red-600">{errors.customerTaxId}</p>
            )}
          </div>
        )}
      </div>

      {user && (
        <div className="mt-5 rounded-xl border border-m-border-light bg-m-canvas-light p-3">
          <p className="text-xs text-m-ink-muted">
            <strong className="font-medium text-m-ink">Note:</strong> Your contact information has been automatically
            filled from your profile. You can edit any field if needed.
          </p>
        </div>
      )}
    </div>
  );
};

export default ContactInfoForm;
